"use client";

import { useEffect, useState } from "react";
import Image from "next/image";

import MentorText from "@/components/global/sections/mentor/MentorText";
import type { MentorCopy } from "@/sanity/pages/PageMentorTemplate";
import Link from "next/link";
import { stegaClean } from "next-sanity";
import { motion, useReducedMotion, type Variants } from "framer-motion";

import { PipeChip } from "@/components/global/sections/courseGallery/_shared";
import { FadeIn, fadeInUp } from "@/components/global/utils/FadeIn";
import { cn } from "@/lib/utils";
import { IMentorCard } from "@/sanity/pages/PageMentorTemplate";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/shadcn/ui/carousel";
import chevronRightIcon from "@/public/mentor/icon-chevron-right.svg";
import MentorIntroDialog from "@/components/global/sections/mentor/MentorIntroDialog";
import playIcon from "@/public/mentor/icon-play.svg";
import { coverImageSizes } from "./imageSizes";
import { mentorProfileAnchorId } from "@/components/global/sections/mentors/MentorDetailsSection";
const cardsStagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

interface MentorsSectionProps {
  eyebrow?: string;
  title?: MentorCopy;
  titleEmphasis?: string;
  mentors?: IMentorCard[];
  profilesPageSlug?: string;
}

export default function MentorsSection({
  eyebrow,
  title,
  titleEmphasis,
  mentors,
  profilesPageSlug,
}: MentorsSectionProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [snapCount, setSnapCount] = useState(0);
  const [copyCount, setCopyCount] = useState(2);
  const mentorCount = mentors?.length ?? 0;
  const [selectedSnap, setSelectedSnap] = useState(0);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!api || !mentorCount) return;
    const update = () => {
      const slideWidth = api.slideNodes()[0]?.offsetWidth;
      if (slideWidth) {
        setCopyCount(
          Math.max(
            2,
            Math.ceil((api.rootNode().clientWidth + slideWidth) / (slideWidth * mentorCount)),
          ),
        );
      }
      setSnapCount(api.scrollSnapList().length);
      setSelectedSnap(api.selectedScrollSnap());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api, mentorCount]);

  if (!mentors?.length) return null;

  const slides = Array.from({ length: copyCount }, (_, copy) =>
    mentors.map((mentor, index) => ({ mentor, index, copy })),
  ).flat();
  const activeMentor = selectedSnap % mentors.length;
  const pageSlug = stegaClean(profilesPageSlug);
  const profileHref = (mentor: IMentorCard) => {
    const profileId = stegaClean(mentor.profileId);
    return pageSlug && profileId
      ? `${pageSlug}#${mentorProfileAnchorId(profileId)}`
      : undefined;
  };

  const arrow = (
    direction: "previous" | "next",
    sizeClassName: string,
    iconClassName: string,
  ) => {
    const isPrevious = direction === "previous";
    return (
      <button
        type="button"
        aria-label={isPrevious ? "Previous mentors" : "Next mentors"}
        disabled={snapCount < 2}
        onClick={() => {
          if (!api) return;
          if (isPrevious)
            api.canScrollPrev()
              ? api.scrollPrev()
              : api.scrollTo(snapCount - 1);
          else api.canScrollNext() ? api.scrollNext() : api.scrollTo(0);
        }}
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] transition-opacity disabled:opacity-40",
          sizeClassName,
        )}
      >
        <Image
          src={chevronRightIcon}
          alt=""
          className={cn(iconClassName, isPrevious && "rotate-180")}
        />
      </button>
    );
  };

  return (
    <section
      id="mentors"
      className="w-full overflow-clip bg-black pt-[8rem] lg:pt-[5.375rem] lg:pb-[20rem]"
    >
      <Carousel
        setApi={setApi}
        opts={{
          loop: true,
          align: (viewSize) => {
            const rem = parseFloat(
              getComputedStyle(document.documentElement).fontSize,
            );
            // Snap to the content grid while cards travel across the full viewport.
            return viewSize >= 993
              ? Math.max(2 * rem, (viewSize - 150 * rem) / 2)
              : 2 * rem;
          },
        }}
        className="flex flex-col gap-[3rem] lg:gap-[6rem]"
      >
        <div className="mx-[2rem] flex flex-col items-center gap-[2rem] lg:mx-auto lg:w-[150rem] lg:flex-row lg:items-end lg:justify-between">
          <FadeIn className="flex flex-col items-center gap-[2rem] lg:items-start">
            {eyebrow && (
              <PipeChip className="!text-[1.75rem] leading-[2.25rem]">
                {eyebrow}
              </PipeChip>
            )}
            {(title || titleEmphasis) && (
              <h2 className="m-0 text-center font-news text-[4.5rem] font-normal !leading-[4.95rem] tracking-[-0.25rem] text-white/90 [text-wrap:balance] lg:text-left lg:text-[6rem] lg:!leading-[6.6rem]">
                <MentorText value={title} />
                {typeof title === "string" && title && titleEmphasis && " "}
                {typeof title === "string" && titleEmphasis && (
                  <em className="italic">{titleEmphasis}</em>
                )}
              </h2>
            )}
          </FadeIn>

          <div className="hidden gap-[1rem] lg:flex">
            {arrow("previous", "h-[6rem] w-[6rem]", "h-[2.5rem] w-[2.5rem]")}
            {arrow("next", "h-[6rem] w-[6rem]", "h-[2.5rem] w-[2.5rem]")}
          </div>
        </div>

        <motion.div
          variants={cardsStagger}
          initial={reduceMotion ? false : "hidden"}
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <CarouselContent className="-ml-[2.5rem] w-full">
            {slides.map(({ mentor, index, copy }) => (
              <CarouselItem
                key={`${copy}-${mentor._key ?? index}`}
                aria-hidden={copy > 0 || undefined}
                className="pl-[2.5rem]"
              >
                <motion.div
                  variants={copy === 0 ? fadeInUp : undefined}
                  className="relative flex h-[63.375rem] w-[35.625rem] flex-col items-center justify-end gap-[1rem] overflow-clip rounded-[1.5rem] border border-black/10 px-[2.5rem] pb-[3rem]"
                >
                  {mentor.portrait && (
                    <Image
                      src={mentor.portrait.src}
                      alt={mentor.name ?? ""}
                      fill
                      quality={100}
                      sizes={coverImageSizes(mentor.portrait, 35.625, 63.375)}
                      draggable={false}
                      className="object-cover"
                    />
                  )}
                  <span className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.2)_50%,#000_100%)]" />

                  {mentor.name && (
                    <p className="relative m-0 text-center font-news text-[4rem] !leading-[4.2rem] tracking-[-0.08rem] text-white">
                      {mentor.name}
                    </p>
                  )}
                  {mentor.role && (
                    <p className="relative m-0 text-center font-geist text-[1.75rem] leading-[2.45rem] text-white/60">
                      {mentor.role}
                    </p>
                  )}
                  {mentor.buttonLabel && mentor.profile?.video ? (
                    <MentorIntroDialog mentor={mentor} tabIndex={copy > 0 ? -1 : undefined} />
                  ) : mentor.buttonLabel && (profileHref(mentor) || mentor.pageUrl) ? (
                    <Link
                      href={profileHref(mentor) || stegaClean(mentor.pageUrl!)}
                      draggable={false}
                      tabIndex={copy > 0 ? -1 : undefined}
                      className="relative flex min-h-[5.5rem] items-center gap-[1rem] rounded-[0.75rem] border border-white/20 bg-white/[0.09] p-[1.25rem] font-geist text-[1.75rem] font-medium leading-[1.75rem] text-white backdrop-blur-[1.65rem] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#98D8FF]"
                    >
                      <Image
                        src={playIcon}
                        alt=""
                        className="h-[2rem] w-[2rem]"
                      />
                      {mentor.buttonLabel}
                    </Link>
                  ) : null}
                </motion.div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </motion.div>
        <div className="flex items-center justify-between px-[2rem] lg:hidden">
          {arrow("previous", "h-[6rem] w-[6rem]", "h-[3rem] w-[3rem]")}
          <div className="flex items-center gap-[1.5rem]">
            {Array.from({ length: Math.min(snapCount, mentors.length) }, (_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to mentor ${i + 1}`}
                aria-current={i === activeMentor}
                onClick={() => api?.scrollTo(i)}
                className={cn(
                  "h-[1rem] rounded-[0.25rem] bg-white transition-[width] duration-300",
                  i === activeMentor ? "w-[4rem]" : "w-[1rem] opacity-30",
                )}
              />
            ))}
          </div>
          {arrow("next", "h-[6rem] w-[6rem]", "h-[3rem] w-[3rem]")}
        </div>
      </Carousel>
    </section>
  );
}
